import { useEffect, useRef, ReactNode, FC } from 'react';
import { X } from 'lucide-react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  closeOnBackdrop?: boolean;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

export const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  maxWidth = 'md',
  closeOnBackdrop = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="backdrop-blur-xs fixed inset-0 bg-black/60 transition-opacity"
        onClick={() => closeOnBackdrop && onClose()}
        aria-hidden="true"
      />

      {/* Modal Dialog Content */}
      <div
        ref={modalRef}
        className={`relative w-full ${maxWidthClasses[maxWidth]} z-10 rounded-2xl border border-surface-border-light bg-surface-panel-light text-content-main-light shadow-2xl transition-all dark:border-surface-border-dark dark:bg-surface-panel-dark dark:text-content-main-dark`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-surface-border-light px-5 py-3.5 dark:border-surface-border-dark">
          <div>
            {title && (
              <h3 className="text-sm font-semibold text-content-main-light dark:text-content-main-dark">
                {title}
              </h3>
            )}
            {description && (
              <p className="mt-0.5 text-xs text-content-muted-light dark:text-content-muted-dark">
                {description}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="ml-4 cursor-pointer rounded-lg p-1.5 text-content-muted-light transition-colors hover:bg-surface-subtle-light hover:text-content-main-light dark:text-content-muted-dark dark:hover:bg-surface-subtle-dark dark:hover:text-content-main-dark"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 text-xs text-content-main-light dark:text-content-main-dark">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-surface-border-light bg-surface-subtle-light px-5 py-3 dark:border-surface-border-dark dark:bg-surface-subtle-dark">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
